import { paths } from "@/app/paths"

export type LdpPropertySaleType = "Sale" | "Rent"

type SearchParamsGetter = Pick<URLSearchParams, "get">

/**
 * Sale vs rent for LDP / search-by-BUPI, from map-search pathname and URL filters.
 */
export function resolveLdpPropertySaleType(
  pathname: string | null | undefined,
  searchParams?: SearchParamsGetter | null
): LdpPropertySaleType {
  if (pathname?.startsWith(paths.rentBase())) {
    return "Rent"
  }
  if (searchParams?.get("propertySaleType") === "Rent") {
    return "Rent"
  }
  return "Sale"
}

export function parseLdpUniqueListingIdFromQuery(
  resolved?: Record<string, string | string[] | undefined> | null
): string | undefined {
  const fromParam = resolved?.uniqueListingId
  if (typeof fromParam === "string") {
    return fromParam
  }
  const legacyId = resolved?.id
  if (typeof legacyId === "string") {
    return legacyId
  }
  return undefined
}

export function parseLdpPropertySaleTypeFromQuery(
  resolved: Record<string, string | string[] | undefined> | undefined,
  defaultSaleType: LdpPropertySaleType
): LdpPropertySaleType {
  const value = resolved?.propertySaleType
  if (value === "Rent" || value === "Sale") {
    return value
  }
  return defaultSaleType
}

export function parseLdpPropertyTypeFromQuery(
  resolved?: Record<string, string | string[] | undefined> | null
): string | undefined {
  const value = resolved?.propertyType
  return typeof value === "string" && value.length > 0 ? value : undefined
}

type CardComputedForSaleType = {
  PropertySaleType?: string[] | null
} | null | undefined

/**
 * Sale vs rent from an SRP/map card row (`Computed.PropertySaleType`), not the SRP pathname.
 */
export function resolveLdpPropertySaleTypeFromCard(
  computed: CardComputedForSaleType
): LdpPropertySaleType | undefined {
  const value = computed?.PropertySaleType?.[0]
  if (value === "Rent" || value === "Sale") {
    return value
  }
  return undefined
}

/**
 * LDP href for property card clicks: `bupi` + `propertySaleType` from card row only
 * (no `uniqueListingId`). Sale type comes from `Computed.PropertySaleType`, not SRP pathname.
 */
export function buildPropertyCardLdpHref(options: {
  addressSlug: string
  bupi: string
  computed?: CardComputedForSaleType
  searchParams?: SearchParamsGetter | null
}): string {
  return buildLdpHref({
    addressSlug: options.addressSlug,
    bupi: options.bupi,
    propertySaleType: resolveLdpPropertySaleTypeFromCard(options.computed),
    searchParams: options.searchParams,
  })
}

export function buildLdpHref(options: {
  addressSlug: string
  bupi: string
  pathname?: string | null
  searchParams?: SearchParamsGetter | null
  /** Autocomplete `!` / `#` listing lookup only — not property cards. */
  uniqueListingId?: string | null
  propertySaleType?: LdpPropertySaleType
  /** Disambiguates BUPI collisions when multiple on-market rows share a BUPI. */
  propertyType?: string | null
  /** Autocomplete disambiguation: `uniqueListingId` only, no `propertySaleType`. */
  omitPropertySaleType?: boolean
}): string {
  const basePath = paths.ldp(options.addressSlug, options.bupi)
  const params = new URLSearchParams()

  if (!options.omitPropertySaleType) {
    const saleType =
      options.propertySaleType ??
      resolveLdpPropertySaleType(options.pathname, options.searchParams)
    if (saleType) {
      params.set("propertySaleType", saleType)
    }
  }

  if (options.propertyType) {
    params.set("propertyType", options.propertyType)
  }

  if (options.uniqueListingId) {
    params.set("uniqueListingId", options.uniqueListingId)
  }

  const sort = options.searchParams?.get("sort")
  if (sort) {
    params.set("sort", sort)
  }

  const qs = params.toString()
  return qs ? `${basePath}?${qs}` : basePath
}

/**
 * LDP href when user selects a Listing from autocomplete.
 * Query prefixes: `!!` → bare LDP; `!` / `#` → disambiguate with uniqueListingId; else SRP context.
 */
export function buildListingAutocompleteHref(
  query: string,
  options: {
    addressSlug: string
    bupi: string
    uniqueListingId: string
    pathname?: string | null
    searchParams?: SearchParamsGetter | null
  }
): string {
  if (query.startsWith("!!")) {
    return paths.ldp(options.addressSlug, options.bupi)
  }

  // `!` / `#` → `bupi` + `uniqueListingId` only (no propertySaleType)
  if (query[0] === "!" || query[0] === "#") {
    return buildLdpHref({
      addressSlug: options.addressSlug,
      bupi: options.bupi,
      uniqueListingId: options.uniqueListingId,
      omitPropertySaleType: true,
    })
  }

  return buildLdpHref({
    addressSlug: options.addressSlug,
    bupi: options.bupi,
    pathname: options.pathname,
    searchParams: options.searchParams,
  })
}
